import { Dispatch } from 'react';
import { FlowConnector } from '../../../../_components/flow-builder/TypeDefs';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { getServiceTypeIcon } from '../../../../_components/journey-builder-footer/utility/Helpers';
import { journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { CreateJourneyStepDetails, CreateJourneyStepEdge, CreateJourneyStepNode } from '../../../../store/_api/TypeDefs';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { FlowConnectorButtonActions, FlowItemConditions, FlowItemDataMap, JourneyStepAction, PayloadTransformation, StepJourneyData, StepJourneyDataMap, ToastMessageHook, TranslateHook } from '../TypeDefs';
import { applyConnectorConditionalExpressionRules } from './AndMergeForkHelpers';
import { generateTooltipTextForConnector } from './ConnectorsHelpers';
import { createEventMapEdges, createEventMapNodes, findConnector, translateLabel } from './PrepareJourneyHelpers';
import eventSchemaIcon from '../../../../assets/images/EventSchema.svg';

/**
 * @method getJourneyStepAction
 * @description Retrieves the journey step action based on the provided status.
 * @param {string} status - The status of the journey step.
 * @returns {JourneyStepAction} The journey step action.
 */
const getJourneyStepAction = (status: string, isCloneJourney: boolean) => {
  if (status === 'ACTIVE' || status === 'INACTIVE') {
    return JourneyStepAction.CREATE;
  }
  if (isCloneJourney) {
    return JourneyStepAction.UPDATE;
  }
  return JourneyStepAction.NONE;
};

/**
 * @method getMaxExecutionTime
 * @description Retrieves the journey step max execution time.
 * @param {number | null} eventMapMaxExecutionTimeInSec.
 * @returns {eventMapMaxExecutionTimeInSec} The journey maximum execution time.
 */
const getMaxExecutionTime = (eventMapMaxExecutionTimeInSec: number | null) => {
  if (eventMapMaxExecutionTimeInSec) {
    return eventMapMaxExecutionTimeInSec;
  }
  return 0;
};

/**
 * @method getIngressNodeId
 * @description Retrieves the step start node of the journey.
 * @param {string | undefined} stepStartNodeId.
 * @param {string} rootItemId.
 * @returns {stepStartNodeId} The journey step start node.
 */
const getIngressNodeId = (stepStartNodeId: string | undefined, rootItemId: string) => {
  if (stepStartNodeId) {
    return stepStartNodeId;
  }
  return rootItemId;
};

/**
 * @method prepareStepJourneyFromData
 * @description Prepares step journey data from the provided data.
 * @param {CreateJourneyStepNode} data - The data for the journey step.
 * @param {FlowItemDataMap} flowItemDataMap - The map of flow item data.
 * @param {string} status - The status of the journey step.
 * @param {boolean} readOnly - if canvas is in view mode
 * @returns {Object | undefined} - The prepared step journey data, or undefined if preparation fails.
 */
export const prepareStepJourneyFromData = (
  {
    data,
    flowItemDataMap,
    status,
    journeyStepDetails,
    stepEdges,
    readOnly,
    isCloneJourney,
    isDesigningCEM
  }:{
    data: CreateJourneyStepNode,
    flowItemDataMap: FlowItemDataMap,
    status: string,
    journeyStepDetails: CreateJourneyStepDetails,
    stepEdges: CreateJourneyStepEdge[],
    readOnly: boolean,
    isCloneJourney: boolean,
    isDesigningCEM: boolean
  }
): {
  step: StepJourneyData,
  payloadTransformations: PayloadTransformation[],
} | undefined => {
  const stepDetails = journeyStepDetails[data.stepName];
  if (!stepDetails) {
    return undefined;
  }
  // check if first step or not, if step node is not part of any edge as egress then its first step
  const isFirstStep = !stepEdges.find((edge) => !!edge.stepEgress.find((egress) => egress.stepNodeId === data.stepNodeId));
  const stepStartNode = stepDetails.startNode;
  // append the start node so that it gets processed as part of regular eventmap, only first first step
  if (stepStartNode?.nodeId && isFirstStep) {
    journeyStepDetails[data.stepName].eventMapNodes.unshift(stepStartNode);
  }
  const { items, flowItemMap, egressCountMap } = createEventMapNodes(stepDetails, flowItemDataMap, readOnly);
  const { connectors, payloadTransformations } = createEventMapEdges(stepDetails, flowItemDataMap, egressCountMap, readOnly, isDesigningCEM);

  const stepStartNodeId = stepStartNode?.nodeId;
  const rootItemId = Object.keys(egressCountMap).find((k) => k !== stepStartNodeId && egressCountMap[k] === 0);
  if (!rootItemId) {
    return undefined;
  }
  const rootItem = flowItemMap[rootItemId];

  if (!rootItem) {
    return undefined;
  }

  // if 2nd step then always the first connector is vertical
  const orientation = isFirstStep ? Orientation.HORIZONTAL : Orientation.VERTICAL;
  // prepend the initial connector from start btn or external service
  connectors.unshift({
    fromNodeId: stepStartNodeId ?? '',
    toNodeId: rootItemId,
    orientation: {
      from: orientation,
      to: orientation
    },
    text: flowItemDataMap[rootItemId].ingressEvent,
    displayEventVersion: flowItemDataMap[rootItemId].ingressEventVersion,
    tooltip: generateTooltipTextForConnector(flowItemDataMap[rootItemId].ingressEvent, false, false),
    config: {},
    buttons: [{
      icon: eventSchemaIcon,
      id: FlowConnectorButtonActions.VIEW_EVENT_SCHEMA,
      label: translateLabel('labels.view_event_schema'),
      height: journeyCanvasConstants.flowItemBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight,
    }]
  });
  rootItem.isRoot = true;
  // if first step then mark the startNode as root
  if (isFirstStep && stepStartNode?.nodeId) {
    const startNodeItem = flowItemMap[stepStartNode.nodeId];
    if (startNodeItem) {
      startNodeItem.isRoot = true;
    }
  }

  const andMergeNodeIds = new Set<string>();
  stepDetails.eventMapEdges?.forEach((edge) => {
    if (edge.ingressOperation === FlowItemConditions.AND && edge.egress?.[0]?.nodeId) {
      andMergeNodeIds.add(edge.egress[0].nodeId);
    }
  });
  const flowConnectorsWithRules = applyConnectorConditionalExpressionRules(
    connectors,
    andMergeNodeIds,
    items.map(({ nodeId, conditionText }) => ({ nodeId, conditionText })),
    flowItemDataMap,
    readOnly,
    isDesigningCEM
  );

  return {
    step: {
      flowItems: items,
      flowConnectors: flowConnectorsWithRules,
      ingressNodeId: getIngressNodeId(stepStartNodeId, rootItemId),
      isRootExternal: !!stepStartNodeId,
      journeyStepDescription: data.stepDescription,
      journeyStepName: data.stepName,
      maxExecutionTime: getMaxExecutionTime(journeyStepDetails[data.stepName].eventMapMaxExecutionTimeInSec),
      uniqueIdentifiers: journeyStepDetails[data.stepName].uniqueIdentifier?.split(','),
      journeyStepAction: getJourneyStepAction(status, isCloneJourney),
      startingNode: stepStartNode && !isFirstStep ? { // step startNode in 2nd step onwards will go into startingNode
        nodeId: stepStartNode.nodeId,
        x: journeyCanvasConstants.rootExternalItemPosition.x,
        y: journeyCanvasConstants.rootExternalItemPosition.y,
        name: stepStartNode.serviceName,
        subTitle: stepStartNode.serviceOperationName,
        displayVersion: stepStartNode.serviceVersion,
        description: stepStartNode.serviceDescription,
        buttons: [],
        icon: getServiceTypeIcon(stepStartNode?.serviceType),
        selectedIcon: getServiceTypeIcon(stepStartNode?.serviceType),
        isRoot: false,
        config: { draggable: false },
      } : undefined,
      eventMapName: journeyStepDetails[data.stepName].eventMapName,
    },
    payloadTransformations,
  };
};

/**
 * @method prepareStepConnectorFromData
 * @description Prepares step connectors from the provided data.
 * @param {CreateJourneyStepEdge[]} data - The data for the journey step connectors.
 * @param {StepJourneyDataMap} stepJourneyDataMap - The map of step journey data.
 * @returns {Object} - The prepared step connectors.
 */
export const prepareStepConnectorFromData = (data: CreateJourneyStepEdge[], flowItemDataMap: FlowItemDataMap, stepJourneyDataMap: StepJourneyDataMap): {
  connectors: FlowConnector[]
} => {
  const connectors: FlowConnector[] = [];

  data.forEach((edge) => {
    edge.stepIngress.forEach((i) => {
      edge.stepEgress.forEach((e) => {
        let eventVersion;
        if (stepJourneyDataMap[e.stepNodeId]) {
          const rootNodeId = stepJourneyDataMap[e.stepNodeId].ingressNodeId;
          eventVersion = flowItemDataMap[rootNodeId]?.ingressEventVersion;
        }
        const isConnectorExists = findConnector(connectors, i.stepNodeId, e.stepNodeId);
        if (stepJourneyDataMap[i.stepNodeId] && stepJourneyDataMap[e.stepNodeId] && !isConnectorExists) {
          connectors.push({
            fromNodeId: i.stepNodeId,
            toNodeId: e.stepNodeId,
            orientation: {
              from: Orientation.HORIZONTAL,
              to: Orientation.HORIZONTAL
            },
            text: e.eventName,
            displayEventVersion: eventVersion,
            tooltip: e.eventName,
            config: {},
            buttons: []
          });
        }
      });
    });
  });

  return {
    connectors,
  };
};

/**
 * @method initiateStepClickHandler
 * @description Handles the click event for the initiate step card.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const initiateStepClickHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({
    type: Actions.CLICK_INITIATE_STEP_CARD
  });
};

/**
 * @method saveStepModalHandler
 * @description Saves the step journey modal.
 * @param {string} journeyStepName - The name of the journey step.
 * @param {{ hours: string, minutes: string, seconds: string }} maxExecutionTimeInput - The input for the maximum execution time.
 * @param {string[]} uniqueIdentifiers - The unique identifiers for the journey step.
 * @param {string | undefined} journeyStepDescription - The description of the journey step.
 * @param {ToastMessageHook} toastMessage - The toast message hook.
 * @param {TranslateHook} translate - The translate hook.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {StepJourneyDataMap} stepJourneyDataMap - The map of step journey data.
 * @param {{ dropdownEndpointSelected: boolean; sidebarInvalidSearch: boolean; }} validation - The validation data.
 */
export const saveStepModalHandler = (
  {
    journeyStepName,
    maxExecutionTimeInput,
    uniqueIdentifiers,
    journeyStepDescription,
    endpointNodeId,
    isEdit,
    toastMessage,
    translate,
    dispatch,
    stepJourneyDataMap,
  }: {
    journeyStepName: string,
    maxExecutionTimeInput: { hours: string, minutes: string, seconds: string },
    uniqueIdentifiers: string[],
    journeyStepDescription: string | undefined,
    endpointNodeId: string | undefined,
    isEdit: boolean,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    stepJourneyDataMap: StepJourneyDataMap,
  }
) => {
  // check step name is unique or not
  const name = journeyStepName;
  const duplicate = !isEdit && !Object.keys(stepJourneyDataMap).every((k) => {
    const step = stepJourneyDataMap[k];
    return step.journeyStepName !== name;
  });
  if (duplicate) {
    toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_journey_step_duplicate_name'));
  } else {
    dispatch({ type: Actions.SET_JOURNEY_STEP_DETAILS, payload: { stepName: journeyStepName, maxExcTime: maxExecutionTimeInput, uniqueIdentifiers, journeyStepDescription, endpointNodeId, isEdit } });
  }
};

/**
 * @method stepCardMenuClickHandler
 * @description Handles the click event for the step card menu.
 * @param {string} id - The ID of the step card.
 * @param {number} mouseX - The X-coordinate of the mouse click.
 * @param {number} mouseY - The Y-coordinate of the mouse click.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const stepCardMenuClickHandler = (id: string, mouseX: number, mouseY: number, dispatch: Dispatch<UpdateActions>) => {
  dispatch({
    type: Actions.SET_STEP_CARD_MENU_COORDINATES,
    payload: {
      mouseX,
      mouseY,
      nodeId: id
    }
  });
};

/**
 * @method closeStepModalHandler
 * @description close the step journey modal
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const closeStepModalHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.CLOSE_JOURNEY_STEP_MODAL });
};

/**
 * @method deleteStepCardHandler
 * @description Handles the deletion of a step card.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const deleteStepCardHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.NOTIFY_DELETE_STEP });
};

/**
 * @method dismissStepCardMenuDropdown
 * @description Dismisses the step card menu dropdown.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const dismissStepCardMenuDropdown = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.DISMISS_STEP_CARD_MENU_DROPDOWN });
};

/**
 * @method deleteStep
 * @description Deletes the steps on canvas.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {string | undefined} deletingNodeId - The ID of the node to be deleted.
 */
export const deleteStep = (dispatch: Dispatch<UpdateActions>, deletingNodeId: string | undefined) => {
  dispatch({ type: Actions.DELETE_STEP_ITEM, payload: { nodeId: deletingNodeId ?? '' } });
};

/**
 * @method deleteSubsequentStep
 * @description Deletes the subsequent steps.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {string | undefined} deletingNodeId - The ID of the node to be deleted.
 */
export const deleteSubsequentStep = (dispatch: Dispatch<UpdateActions>, deletingNodeId: string | undefined) => {
  dispatch({ type: Actions.DELETE_SUBSEQUENT_STEPS, payload: { nodeId: deletingNodeId ?? '' } });
};

/**
 * @method editStepDetailsHandler
 * @description Handles the editing of step details.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const editStepDetailsHandler = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.EDIT_STEP_DETAILS });
};
